import { Suspense } from "react"

import { ContentBox, ErrorBoundary, ErrorMessage } from "web/components"

import { useTLORecordingEvents } from "web/hooks/data"

import { formatAsRelativeDate } from "web/services/format"
import { isNonEmptyArray, wrapArray } from "web/services/helpers"
import { humanize } from "web/services/inflector"

import EventCreated from "./EventCreated"
import EventDeleted from "./EventDeleted"
import EventSubjectCreated from "./EventSubjectCreated"
import EntryUpdated from "./EntryUpdated"
import GenericEvent from "./GenericEvent"

function safeComponent({
  Component,
  errorFallback = null,
  suspenseFallback = null,
}) {
  return (props) => {
    return (
      <ErrorBoundary fallback={errorFallback}>
        <Suspense fallback={suspenseFallback}>
          <Component {...props} />
        </Suspense>
      </ErrorBoundary>
    )
  }
}

function EventUpdated({ event }) {
  if (event.recordable_type === "Entry") return <EntryUpdated event={event} />
  return null
}

function EventText({ event }) {
  if (event.action === "created") {
    if (event.recordable_type === "Entry") {
      return <EventSubjectCreated event={event} />
    } else {
      return <EventCreated event={event} />
    }
  } else if (event.action === "deleted") {
    return <EventDeleted event={event} />
  } else if (event.action === "updated") {
    return <EventUpdated event={event} />
  }

  return <GenericEvent event={event} />
}

function EventWithDetails({ event }) {
  return wrapArray(event.change_details).map((change_details, i) => (
    <div className="my-2 ml-2.5 flex items-center space-x-1.5">
      <EventUpdated key={i} event={{ ...event, change_details }} />
    </div>
  ))
}

function EventWithoutDetails({ event }) {
  return (
    <div className="my-2 ml-2.5 flex items-center space-x-1.5">
      <EventText event={event} />
    </div>
  )
}

function EventList() {
  const events = useTLORecordingEvents()

  if (!isNonEmptyArray(events)) return null

  const eventGroups = events.reduce((groups, event) => {
    const timestamp = formatAsRelativeDate({ value: event.created_at })

    const existing = groups.find((el) => el.timestamp === timestamp)
    if (existing) {
      existing.events.push(event)
    } else {
      groups.push({ timestamp, events: [event] })
    }

    return groups
  }, [])

  return (
    <div className="event-list">
      {eventGroups.map((group, i) => (
        <ContentBox key={i} className="mb-2.5">
          <h2 className="neutral-4 font-bold">{humanize(group.timestamp)}</h2>
          <div className="mt-3">
            {group.events.map((event, i) =>
              !!event.change_details ? (
                <EventWithDetails key={i} event={event} />
              ) : (
                <EventWithoutDetails key={i} event={event} />
              )
            )}
          </div>
        </ContentBox>
      ))}
    </div>
  )
}

export default safeComponent({
  Component: EventList,
  errorFallback: (
    <ErrorMessage message="Could not load history. Try again. Contact support if the problem persists." />
  ),
})
